import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaUpload } from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import styles from "./FileUpload.module.scss";

// 5 MB
const maxFileSize = 5242880;

const FileUpload = ({
  name,
  id,
  required,
  handleFileChange,
  submitErrorMessage,
}) => {
  const [uploadedFilename, setUploadedFilename] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const inputContainerRef = useRef(null);

  // runs when file selection is attempted ...
  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      // valid file selection
      if (fileRejections.length === 0) {
        const filename =
          acceptedFiles.length === 0 ? "" : acceptedFiles[0].name;
        setErrorMessage("");
        setUploadedFilename(filename);
        handleFileChange(acceptedFiles[0]);
      }

      // invalid file selection
      else {
        const rejectionError =
          fileRejections.length > 1
            ? "Please upload one file only"
            : fileRejections.length === 0
            ? ""
            : fileRejections[0].file.size > maxFileSize
            ? "File size can't exceed 5MB"
            : "Invalid file type";

        if (rejectionError) {
          setErrorMessage(rejectionError);
          setUploadedFilename("");
          handleFileChange(null);
          inputContainerRef.current.querySelector("input[type='file']").value =
            "";
        }
      }
    },
    [handleFileChange]
  );

  const clearSelection = (event) => {
    event.preventDefault();
    // clearing the file selection
    inputContainerRef.current.querySelector("input[type='file']").value = "";
    handleFileChange(null);
    setUploadedFilename("");
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    // accept: '.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    accept: ".pdf",
    multiple: false,
    maxSize: maxFileSize,
  });

  return (
    <div
      className={`${styles.container} ${
        uploadedFilename ? styles.successContainer : "emptyFileUpload"
      } ${errorMessage ? styles.errorContainer : ""} `}
      tabIndex={0}
      id="fileUploadContainer"
    >
      <div
        {...getRootProps({ className: "dropzone" })}
        className={styles.dropzone}
      >
        <div className={styles.logoAndInput} ref={inputContainerRef}>
          <input
            {...getInputProps()}
            className={styles.input}
            id={id}
            name={name}
            required={required}
          />
          <FaUpload
            size="30px"
            color={errorMessage ? "red" : uploadedFilename ? "green" : "#ccc"}
          />
        </div>

        {uploadedFilename ? (
          <div className={styles.successInfoContainer}>
            <p className={styles.fileInfo}>{uploadedFilename}</p>
            <RiDeleteBin5Line
              onClick={clearSelection}
              className={styles.clearButton}
            />
          </div>
        ) : (
          <div className={styles.instructions}>
            <p style={{ fontSize: "1.3rem" }}>
              Drag and drop a file here (PDF only)
            </p>

            <button
              type="button"
              className={styles.button}
              onClick={open}
              disabled={submitErrorMessage}
            >
              Browse File
            </button>
            {errorMessage && <p className={styles.error}>{errorMessage}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

FileUpload.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  handleFileChange: PropTypes.func.isRequired,
};

export { FileUpload };
